"use client";

import { useEffect, useState } from "react";
import { Hammer, Menu, Phone, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  if (loading) {
    return <div className="flex items-center gap-3">
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i} className="flex items-center relative pb-2">
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)} className={`group relative rounded px-3 py-1.5 text-sm font-normal capitalize text-foreground hover:text-primary focus:text-primary transition-colors duration-200 ${hoveredIndex === i ? "bg-gray-100 dark:bg-gray-900" : "bg-transparent hover:bg-gray-100 dark:hover:bg-gray-900"}`}>
                <span className="relative">
                  {item.label}
                </span>
                {hoveredIndex === i && <Search className="absolute left-full ml-2 w-4 h-4 text-[#FFA500]" />}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 px-6">
        <div className="h-6 w-32 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-6 w-32 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-6 w-32 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-6 w-32 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="group flex items-center gap-3 rounded px-3 py-3 text-foreground hover:bg-gray-100 dark:hover:bg-gray-900 hover:text-primary transition-colors duration-200">
          <span className="text-sm font-normal capitalize">
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              bottom: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1636" id="header" className="sticky top-0 z-50 w-full bg-background text-foreground">
      <div className={`relative w-full border-b transition-all duration-200 ${scrolled ? "bg-background shadow-md border-[#FFA500]" : "bg-background border-transparent"}`}>
        <div className="mx-auto flex h-16 md:h-[76px] max-w-[1360px] items-center justify-between px-4 sm:px-6 lg:px-12">
          <div className="flex items-center gap-8 lg:gap-10">
            <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="Asahi Search home">
              <WvcLogo className="!w-[200px] !h-[30px] [&_svg]:!w-[200px] [&_svg]:!h-[30px]" />
            </Link>

            <div className="hidden lg:flex">
              <MenuProvider menu_id="46">
                <DesktopMenuConsumer />
              </MenuProvider>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button asChild className="hidden md:inline-flex h-11 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-6 text-xs font-bold uppercase tracking-[0.12em] font-default shadow-md hover:shadow-sm transition-all duration-150 active:translate-x-[2px] active:translate-y-[2px]">
              <Link to="tel:+14753326003">Call Us Today</Link>
            </Button>

            <Button asChild size="icon" className="md:hidden h-10 w-10 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-all duration-150 active:translate-x-[1px] active:translate-y-[1px]" aria-label="Start a Project">
              <Link to="/ContactUsPage">
                <Phone className="size-5" />
              </Link>
            </Button>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden h-10 w-auto px-3 rounded-lg border border-border hover:bg-accent hover:text-accent-foreground text-foreground" aria-label="Open menu">
                  <span className="text-xs font-bold uppercase tracking-[0.18em] font-default">
                    [ Menu ]
                  </span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-sm bg-background text-foreground border-l border-primary/40 p-0">
                <div className="flex flex-col h-full pt-6">
                  <div className="flex items-center justify-between px-6 pb-6 border-b border-border">
                    <SheetTitle asChild>
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] font-default text-muted-foreground">
                        <span className="text-primary">ASAHI</span> No.01 — Menu
                      </span>
                    </SheetTitle>
                  </div>

                  <div className="flex-1 overflow-y-auto py-6">
                    <MenuProvider menu_id="46">
                      <MobileMenuConsumer onClose={() => setOpen(false)} />
                    </MenuProvider>
                  </div>

                  <div className="px-6 pb-8 pt-4 border-t border-border">
                    <Button asChild className="w-full h-12 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-bold uppercase tracking-[0.12em] font-default shadow-md" onClick={() => setOpen(false)}>
                      <Link to="/ContactUsPage">Start a Project</Link>
                    </Button>
                    <p className="mt-4 text-[0.6875rem] font-default tracking-[0.12em] uppercase text-muted-foreground">
                      Mon-Fri: 9am — 5pm
                    </p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}